import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { PROJECTS } from "./data";
import { scrollToId } from "./hooks";

const FILTERS = ["All Projects", "Commercial", "Industrial", "Domestic"];

export function Projects() {
  const [filter, setFilter] = useState("All Projects");
  const [lightbox, setLightbox] = useState(null);

  const visible = PROJECTS.filter((p) => filter === "All Projects" || p.cat === filter);

  return (
    <section id="projects" className="bg-[#111923] text-[#eef4f8] py-24 md:py-32 border-b border-[#213042]" data-testid="projects-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 reveal">
          <div>
            <span className="font-mono-code text-xs text-[#00c2a8] uppercase tracking-widest block mb-2">// TRACK RECORD</span>
            <h2 className="font-mono-code text-[clamp(26px,3.8vw,48px)] font-bold uppercase text-white leading-tight">
              Trusted To Deliver.<br />Proven On Major Projects.
            </h2>
          </div>
          <p className="text-xs text-[#a2b8cc] max-w-sm leading-relaxed">
            From prestigious retail developments to major industrial facilities and private homes, our work
            demonstrates the scale and versatility of our services.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 mb-12 reveal" role="group" aria-label="Filter projects">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`font-mono-code text-[11px] uppercase tracking-wider px-4 py-2 rounded border transition-colors ${
                filter === f
                  ? "bg-[#00c2a8] text-[#111923] border-[#00c2a8] font-bold"
                  : "border-[#30455e] text-[#a2b8cc] hover:border-[#00c2a8] hover:text-white"
              }`}
              aria-pressed={filter === f}
              data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="projects-grid">
          {visible.map((p) => (
            <article key={p.id} className="reveal bg-[#182330] border border-[#213042] rounded p-6 hover:border-[#00c2a8]/50 transition-colors" data-testid={`project-card-${p.id}`}>
              <button
                className="block w-full text-left"
                onClick={() => setLightbox(p)}
                aria-label={`Enlarge photograph: ${p.title}`}
                data-testid={`project-image-btn-${p.id}`}
              >
                <figure className="relative aspect-[16/10] rounded mb-2 overflow-hidden border border-[#213042] group">
                  <img
                    src={p.img.src}
                    alt={p.img.alt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  <figcaption className="absolute bottom-0 inset-x-0 bg-[#111923]/85 backdrop-blur px-4 py-2.5 font-mono-code text-[10px] uppercase tracking-widest text-[#00c2a8]">
                    {p.kicker}
                    {p.placeholder && <span className="text-[#7d96ac] normal-case tracking-normal"> &mdash; illustrative image</span>}
                  </figcaption>
                </figure>
              </button>
              <span className="font-mono-code text-[10px] text-[#7d96ac]">{p.n}</span>
              <h3 className="font-mono-code text-xl font-bold uppercase text-white mt-1 mb-1">{p.title}</h3>
              <span className="font-mono-code text-[10px] uppercase tracking-widest text-[#00c2a8] block mb-2">{p.location}</span>
              <p className="text-xs text-[#a2b8cc] leading-relaxed">{p.desc}</p>
            </article>
          ))}
        </div>

        <div className="mt-12 text-center reveal">
          <p className="text-xs text-[#7d96ac] mb-4">More project photography is being added as our portfolio grows.</p>
          <button
            onClick={() => scrollToId("contact")}
            className="font-mono-code bg-[#00c2a8] text-[#111923] font-bold text-xs px-8 py-4 rounded uppercase tracking-wider hover:bg-white transition-colors"
            data-testid="projects-quote-btn"
          >
            Discuss Your Project
          </button>
        </div>
      </div>

      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent
          className="bg-[#111923] border-[#213042] text-white max-w-4xl w-[95vw] p-4 sm:p-6"
          data-testid="project-lightbox"
        >
          {lightbox && (
            <>
              <DialogTitle className="font-mono-code text-sm uppercase tracking-wider text-white pr-8">
                {lightbox.title} <span className="text-[#00c2a8]">&bull; {lightbox.location}</span>
              </DialogTitle>
              <img
                src={lightbox.img.src}
                alt={lightbox.img.alt}
                className="w-full max-h-[70vh] object-contain rounded border border-[#213042]"
                data-testid="lightbox-image"
              />
              <p className="text-xs text-[#a2b8cc] leading-relaxed">{lightbox.desc}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
